import {useRef, useState} from "react";
import {useLocale, useMessages} from "i18n";
import {transferApi} from "../../api/transferApi";
import {statsIdFromShareUrl} from "./shareUrlStatsId";
import {Plan} from "../gating";
import {normalizeEmails, resolveEmailInsteadSend} from "./createFormLogic";
import {recipientLimitMessage} from "./recipientLimitMessage";
import {useSingleFlight} from "../../utils/useSingleFlight";
import {DeliveryNotice, summarizeDelivery} from "../../core/transfer/deliveryOutcome";

/**
 * "Email instead" logic for the SERVER success screen (ticket #51). By the time this screen shows the
 * transfer is already uploaded to S3, so emailing it is the honest post-hoc notify:
 * `POST /transfer/uploads/{transferId}/email` (see {@link transferApi.emailTransfer}). The backend
 * decodes the encoded transfer id from the download URL's last path segment — the exact value
 * {@link statsIdFromShareUrl} derives — so no new id needs threading through the transfer service.
 *
	 * Pure logic (no markup): the dumb success view binds recipient inputs to {@code toEmails}. The
	 * authenticated account supplies canonical sender identity on the server.
 */
export type EmailInsteadPhase = "idle" | "compose" | "sent";

export interface EmailInstead {
	phase: EmailInsteadPhase;
	sending: boolean;
	toEmails: string[];
	setToEmails: (emails: string[]) => void;
	/** Reveal recipient inputs, or request authentication before protected delivery is offered. */
	open: () => void;
	/** Collapse back to the two-up actions without sending. */
	cancel: () => void;
	/** True when there is at least one recipient and no send in flight. */
	canSend: boolean;
	/**
	 * Emails the link and reports who actually received it (ui#369); `null` means authentication was
	 * requested before any protected API call, so nothing was sent.
	 */
	send: () => Promise<DeliveryNotice | null>;
	/** Outcome of the last completed send — what the "sent" confirmation reports. */
	notice: DeliveryNotice | null;
}

export function useEmailInstead(
	url: string,
	plan: Plan,
	initialRecipientEmails: string[],
	isAuthenticated: boolean,
	/**
	 * Requests authentication and reports whether it landed — the login modal's own contract
	 * (`login/LoginModalProvider`). Typed with its OUTCOME rather than as `() => void` (ui#1242): a
	 * void seam here is what silently discarded the sender's intent, so both gates below resume
	 * instead of making them press the same button a second time. `false` resumes nothing.
	 */
	onAuthenticationRequired: () => Promise<boolean>,
): EmailInstead {
	const {sender, pricing} = useMessages();
	const {locale} = useLocale();
	const {emailErrors} = sender.success;
	const [phase, setPhase] = useState<EmailInsteadPhase>("idle");
	const [sending, setSending] = useState(false);
	const sendFlight = useSingleFlight();
	const [toEmails, setToEmails] = useState<string[]>([]);
	const [knownRecipientEmails, setKnownRecipientEmails] = useState(
		() => normalizeEmails(initialRecipientEmails),
	);
	const [notice, setNotice] = useState<DeliveryNotice | null>(null);

	const verdict = resolveEmailInsteadSend({
		knownEmails: knownRecipientEmails,
		newEmails: toEmails,
		sending,
		plan,
	});
	// The TIER as it stands now, not as it stood when Send was pressed. A resumed send re-decides from
	// the closure of a press made while the session was gone, where `plan` reads "free" — so the
	// captured verdict would measure a PLUS account's recipients against the free cap and refuse a list
	// its plan allows (ui#1242).
	const planRef = useRef(plan);
	planRef.current = plan;

	// `canSend` folds in `sending`, but that is React state — it reaches the DOM a render after the
	// click, and this button has no unmount behind it to fall back on. Hammering Send emailed the
	// recipients once per click (ui#360).
	const send = (): Promise<DeliveryNotice | null> => sendFlight.run(async () => {
		if (!isAuthenticated) {
			if (!(await onAuthenticationRequired())) {
				// Dismissed: back to the two-up actions, exactly where the press found them. Collapsing
				// only HERE, rather than before the modal opens, is what lets the resume below finish the
				// send the sender already asked for instead of dropping them back at "Email instead".
				setPhase("idle");
				return null;
			}
		}
		const transferId = statsIdFromShareUrl(url);
		if (!transferId) {
			throw new Error(emailErrors.noTransfer);
		}
		// Re-decided against the tier on hand, for the resumed case above. Identical to `verdict` on the
		// ordinary path — the same rule over the same inputs — so this is one decision, not two.
		const sendable = resolveEmailInsteadSend({
			knownEmails: knownRecipientEmails,
			newEmails: toEmails,
			sending,
			plan: planRef.current,
		});
		if (sendable.rejection === "missing") {
			throw new Error(emailErrors.missing);
		}
		if (sendable.rejection === "overLimit") {
			throw new Error(recipientLimitMessage(
				planRef.current,
				pricing.planNames,
				pricing.compare.unlimited,
				sender.recipients,
				locale,
			));
		}
		setSending(true);
		try {
			// One chokepoint call, so one response — the domain still owns the verdict so this screen
			// and the post-upload one cannot drift on what "sent" means.
			const sent = summarizeDelivery([await transferApi.emailTransfer(transferId, sendable.payload)]);
			setKnownRecipientEmails(sendable.cumulativeEmails);
			setToEmails([]);
			setNotice(sent);
			setPhase("sent");
			return sent;
		} finally {
			setSending(false);
		}
	});

	return {
		phase,
		sending,
		toEmails,
		setToEmails,
		open: () => {
			if (!isAuthenticated) {
				// Reveals the recipient inputs the press asked for as soon as the sign-in lands, rather
				// than leaving the sender back at an unchanged screen wondering what logging in achieved.
				void onAuthenticationRequired().then(signedIn => {
					if (signedIn) {
						setPhase("compose");
					}
				});
				return;
			}
			setPhase("compose");
		},
		cancel: () => setPhase("idle"),
		canSend: verdict.canSend,
		send,
		notice,
	};
}
